import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/auth.store'
import { apiClient } from '@/lib/api-client'
import { AdminLayout } from '@/components/AdminLayout'
import { VisitOutcome } from '@shared/types/entities'

interface ScoutOption {
  id: string
  email: string
  firstName: string
  lastName: string
}

interface VisitItem {
  id: string
  scoutId: string
  businessId: string
  businessName?: string
  startTime: string
  endTime?: string
  duration?: number
  notes?: string
  outcome?: VisitOutcome
  isCompleted: boolean
  startLatitude: number
  startLongitude: number
  endLatitude?: number
  endLongitude?: number
  photoUrls?: string[]
  distanceMeters?: number
  distanceOverridden?: boolean
  rejected?: boolean
  rejectedAt?: string
  rejectionReason?: string
  compensationId?: string
}

export function VisitControlPage() {
  const { t } = useTranslation()
  const { token } = useAuthStore()
  const queryClient = useQueryClient()
  const [selectedScoutId, setSelectedScoutId] = useState<string>('')
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [selectedVisit, setSelectedVisit] = useState<VisitItem | null>(null)
  const [signedPhotoUrls, setSignedPhotoUrls] = useState<string[]>([])
  const [rejectReason, setRejectReason] = useState('')
  const [showRejectForm, setShowRejectForm] = useState(false)

  useEffect(() => {
    setShowRejectForm(false)
    setRejectReason('')
    if (!selectedVisit?.photoUrls?.length || !token) {
      setSignedPhotoUrls([])
      return
    }
    apiClient.post(`/visits/${selectedVisit.id}/photos`, { urls: selectedVisit.photoUrls }, token)
      .then((res: any) => setSignedPhotoUrls(res.signedUrls || []))
      .catch(() => setSignedPhotoUrls([]))
  }, [selectedVisit?.id, token])

  const rejectMutation = useMutation({
    mutationFn: (visitId: string) =>
      apiClient.post(`/admin/visits/${visitId}/reject`, { reason: rejectReason }, token!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'visits'] })
      setSelectedVisit(null)
      setShowRejectForm(false)
      setRejectReason('')
    },
  })

  const { data: scoutsData } = useQuery({
    queryKey: ['admin', 'scouts'],
    queryFn: () => apiClient.get('/admin/scouts', token!) as Promise<{ scouts: ScoutOption[] }>,
    enabled: !!token,
  })

  const { data: visitsData, isLoading: visitsLoading } = useQuery({
    queryKey: ['admin', 'visits', selectedScoutId],
    queryFn: () => apiClient.get(`/admin/visits${selectedScoutId ? `?scoutId=${selectedScoutId}` : ''}`, token!) as Promise<{ visits: VisitItem[] }>,
    enabled: !!token,
  })

  const scouts: ScoutOption[] = scoutsData?.scouts || []
  const allVisits: VisitItem[] = visitsData?.visits || []
  const visits = selectedMonth
    ? allVisits.filter(v => v.startTime.startsWith(selectedMonth))
    : allVisits

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    if (hours > 0) return `${hours}h ${mins}m`
    return `${mins}m`
  }

  const getOutcomeColor = (outcome?: VisitOutcome) => {
    switch (outcome) {
      case VisitOutcome.REGISTERED: return 'bg-green-100 text-green-800'
      case VisitOutcome.INTERESTED: return 'bg-blue-100 text-blue-800'
      case VisitOutcome.CALLBACK_REQUIRED: return 'bg-yellow-100 text-yellow-800'
      case VisitOutcome.NOT_INTERESTED: return 'bg-gray-100 text-gray-800'
      case VisitOutcome.CLOSED: return 'bg-red-100 text-red-800'
      case VisitOutcome.NOT_FOUND: return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getScoutName = (scoutId: string) => {
    const scout = scouts.find(s => s.id === scoutId)
    return scout ? `${scout.firstName} ${scout.lastName}` : scoutId.slice(0, 8)
  }

  const completedVisits = visits.filter(v => v.isCompleted)
  const activeVisits = visits.filter(v => !v.isCompleted)
  const totalHours = completedVisits.reduce((sum, v) => sum + (v.duration || 0), 0) / 3600

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.visitControl')}</h1>
          <p className="text-gray-600">{t('admin.visitControlDesc')}</p>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Scout</label>
              <select
                value={selectedScoutId}
                onChange={(e) => setSelectedScoutId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md min-w-[250px]"
              >
                <option value="">{t('admin.allScouts')}</option>
                {scouts.map((scout) => (
                  <option key={scout.id} value={scout.id}>
                    {scout.firstName} {scout.lastName} ({scout.email})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.month')}</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('admin.totalVisits')}</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">{visits.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('scout.completedVisits')}</div>
            <div className="text-2xl font-bold text-green-600 mt-1">{completedVisits.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('scout.activeVisits')}</div>
            <div className="text-2xl font-bold text-blue-600 mt-1">{activeVisits.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-sm text-gray-600">{t('compensation.totalHours')}</div>
            <div className="text-2xl font-bold text-purple-600 mt-1">{totalHours.toFixed(1)}h</div>
          </div>
        </div>

        {/* Visits Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">{t('admin.visits')}</h2>
          </div>
          <div className="overflow-x-auto">
            {visitsLoading ? (
              <div className="p-6 text-center text-gray-500">{t('common.loading')}</div>
            ) : visits.length === 0 ? (
              <div className="p-6 text-center text-gray-500">{t('admin.noVisitsFound')}</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scout</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.businesses')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('admin.duration')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('scout.visitOutcome')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('scout.visitNotes')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('common.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {visits.map((visit) => (
                    <tr key={visit.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedVisit(visit)}>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {getScoutName(visit.scoutId)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {visit.businessName || visit.businessId.slice(0, 8)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(visit.startTime).toLocaleDateString('de-DE')}
                        <br />
                        <span className="text-xs">{new Date(visit.startTime).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {visit.duration ? formatDuration(visit.duration) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        {visit.outcome ? (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getOutcomeColor(visit.outcome)}`}>
                            {t(`scout.outcome.${visit.outcome.toLowerCase()}`)}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                        {visit.notes || '-'}
                      </td>
                      <td className="px-6 py-4">
                        {visit.rejected ? (
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('admin.rejected')}
                          </span>
                        ) : (
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${visit.isCompleted ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                            {visit.isCompleted ? t('admin.completed') : t('admin.active')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* Visit Detail Modal */}
      {selectedVisit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedVisit(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedVisit.businessName || selectedVisit.businessId.slice(0, 8)}
              </h3>
              <button onClick={() => setSelectedVisit(null)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
            </div>

            <div className="p-6 space-y-4">
              {/* Meta info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase">Scout</div>
                  <div className="text-sm font-medium">{getScoutName(selectedVisit.scoutId)}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">{t('common.status')}</div>
                  <span className={`px-2 py-1 text-xs font-semibold rounded-full ${selectedVisit.isCompleted ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                    {selectedVisit.isCompleted ? t('admin.completed') : t('admin.active')}
                  </span>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase">{t('scout.startedAt')}</div>
                  <div className="text-sm">{new Date(selectedVisit.startTime).toLocaleString('de-DE')}</div>
                </div>
                {selectedVisit.endTime && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase">{t('admin.endTime')}</div>
                    <div className="text-sm">{new Date(selectedVisit.endTime).toLocaleString('de-DE')}</div>
                  </div>
                )}
                {selectedVisit.duration !== undefined && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase">{t('admin.duration')}</div>
                    <div className="text-sm font-medium">{formatDuration(selectedVisit.duration)}</div>
                  </div>
                )}
                {selectedVisit.outcome && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase">{t('scout.visitOutcome')}</div>
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getOutcomeColor(selectedVisit.outcome)}`}>
                      {t(`scout.outcome.${selectedVisit.outcome.toLowerCase()}`)}
                    </span>
                  </div>
                )}
                {selectedVisit.distanceMeters !== undefined && (
                  <div>
                    <div className="text-xs text-gray-500 uppercase">{t('scout.distance')}</div>
                    <div className="text-sm">
                      {selectedVisit.distanceMeters < 1000
                        ? `${Math.round(selectedVisit.distanceMeters)} m`
                        : `${(selectedVisit.distanceMeters / 1000).toFixed(1)} km`}
                      {selectedVisit.distanceOverridden && (
                        <span className="ml-2 text-xs text-amber-600">({t('scout.overridden')})</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* GPS Coordinates */}
              <div className="border-t pt-4">
                <div className="text-xs text-gray-500 uppercase mb-1">GPS</div>
                <div className="text-sm text-gray-600 font-mono">
                  Start: {selectedVisit.startLatitude.toFixed(6)}, {selectedVisit.startLongitude.toFixed(6)}
                  {selectedVisit.endLatitude && selectedVisit.endLongitude && (
                    <><br />Ende: {selectedVisit.endLatitude.toFixed(6)}, {selectedVisit.endLongitude.toFixed(6)}</>
                  )}
                </div>
              </div>

              {/* Notes */}
              {selectedVisit.notes && (
                <div className="border-t pt-4">
                  <div className="text-xs text-gray-500 uppercase mb-1">{t('scout.visitNotes')}</div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap bg-gray-50 rounded-lg p-3">
                    {selectedVisit.notes}
                  </div>
                </div>
              )}

              {/* Photos */}
              {selectedVisit.photoUrls && selectedVisit.photoUrls.length > 0 && (
                <div className="border-t pt-4">
                  <div className="text-xs text-gray-500 uppercase mb-2">{t('scout.photos')} ({selectedVisit.photoUrls.length})</div>
                  {signedPhotoUrls.length === 0 ? (
                    <div className="text-sm text-gray-500">{t('common.loading')}</div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {signedPhotoUrls.map((url, idx) => (
                        <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="block">
                          <img
                            src={url}
                            alt={`${t('scout.photos')} ${idx + 1}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200 hover:opacity-80 transition-opacity"
                          />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Rejected info */}
              {selectedVisit.rejected && (
                <div className="border-t pt-4">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{t('admin.rejected')}</span>
                      {selectedVisit.rejectedAt && (
                        <span className="text-xs text-gray-500">{new Date(selectedVisit.rejectedAt).toLocaleString('de-DE')}</span>
                      )}
                    </div>
                    {selectedVisit.rejectionReason && (
                      <div className="text-sm text-red-800 mt-2">{selectedVisit.rejectionReason}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Reject action */}
              {selectedVisit.isCompleted && !selectedVisit.rejected && !selectedVisit.compensationId && (
                <div className="border-t pt-4">
                  {!showRejectForm ? (
                    <button
                      onClick={() => setShowRejectForm(true)}
                      className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                    >
                      {t('admin.rejectVisit')}
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('admin.rejectionReason')}</label>
                        <textarea
                          value={rejectReason}
                          onChange={(e) => setRejectReason(e.target.value)}
                          placeholder={t('admin.rejectionReasonPlaceholder')}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-red-500 focus:border-red-500"
                        />
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => rejectMutation.mutate(selectedVisit.id)}
                          disabled={!rejectReason.trim() || rejectMutation.isPending}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {rejectMutation.isPending ? t('admin.processing') : t('admin.confirmReject')}
                        </button>
                        <button
                          onClick={() => { setShowRejectForm(false); setRejectReason('') }}
                          className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-200"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  )
}
